import type { Point } from '../lib/readingColumns';
import { severityOf, type ResolvedThreshold } from '../lib/thresholds';

/** Tiny inline trend line — no axes. Points outside the normal band are marked. */
export default function Sparkline({
  points, threshold, width = 132, height = 40,
}: { points: Point[]; threshold?: ResolvedThreshold | null; width?: number; height?: number }) {
  if (points.length < 2) {
    return <svg viewBox={`0 0 ${width} ${height}`} className="sparkline" role="img" aria-hidden />;
  }
  const vs = points.map((p) => p.v);
  const ts = points.map((p) => p.t);
  let vMin = Math.min(...vs), vMax = Math.max(...vs);
  if (vMin === vMax) { vMin -= 1; vMax += 1; }
  const tMin = Math.min(...ts), tMax = Math.max(...ts);
  const pad = 3;
  const x = (t: number) => pad + ((t - tMin) / (tMax - tMin || 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - ((v - vMin) / (vMax - vMin)) * (height - pad * 2);

  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const worst = points.reduce<'normal' | 'warning' | 'critical'>((acc, p) => {
    const sv = severityOf(p.v, threshold);
    return sv === 'critical' ? 'critical' : sv === 'warning' && acc === 'normal' ? 'warning' : acc;
  }, 'normal');
  const stroke = worst === 'critical' ? 'var(--bad)' : worst === 'warning' ? 'var(--warn)' : 'var(--brand)';

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="sparkline" role="img">
      <path d={`${d} L${x(points[points.length - 1].t).toFixed(1)},${height} L${x(points[0].t).toFixed(1)},${height} Z`}
        fill={stroke} opacity={0.08} />
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => {
        const sv = severityOf(p.v, threshold);
        if (sv === 'normal') return null;
        return <circle key={i} cx={x(p.t)} cy={y(p.v)} r={2.4} fill={sv === 'critical' ? 'var(--bad)' : 'var(--warn)'} />;
      })}
      <circle cx={x(points[points.length - 1].t)} cy={y(points[points.length - 1].v)} r={2.6} fill={stroke} />
    </svg>
  );
}
